import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { api, fmtFecha, initials, colones as fmtCurrency } from "@/lib/api";
import { UserCog, ArrowUpCircle, ArrowDownCircle, ArrowLeftRight, UserX, UserMinus, Plus, Calendar, ShieldCheck, Trash2, CheckCircle2, RotateCcw, AlertTriangle, Search, Coffee, Upload, FileText, CalendarDays, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const ACCION_TIPOS = [
  {
    key: "ascenso",
    title: "Ascenso",
    subtitle: "Promoción a un nuevo puesto/salario con período de prueba de 3 meses.",
    Icon: ArrowUpCircle,
    color: "text-primary-color",
    bg: "bg-[#E6EFEA]",
    border: "border-primary-c",
    badgeCls: "np-badge-info",
    rowBorder: "border-primary-c",
  },
  {
    key: "cambio_puesto",
    title: "Cambio de Puesto",
    subtitle: "Movimiento lateral a un nuevo puesto (opcionalmente con nuevo salario). Sin período de prueba.",
    Icon: ArrowLeftRight,
    color: "text-[#2A6D9C]",
    bg: "bg-[#E7F1F9]",
    border: "border-[#B7D4EA]",
    badgeCls: "np-badge-info",
    rowBorder: "border-[#B7D4EA]",
  },
  {
    key: "descenso",
    title: "Descenso de Puesto",
    subtitle: "Reasignación a un puesto de menor rango/salario. Aplica de forma definitiva.",
    Icon: ArrowDownCircle,
    color: "text-[#B26A00]",
    bg: "bg-[#FFF3E0]",
    border: "border-[#F0DDA0]",
    badgeCls: "np-badge-warning",
    rowBorder: "border-[#F0DDA0]",
  },
  {
    key: "renuncia",
    title: "Renuncia",
    subtitle: "El trabajador termina la relación laboral por su propia decisión. Pasa a Inactivo.",
    Icon: UserMinus,
    color: "text-[#7A4C9E]",
    bg: "bg-[#F1E8F7]",
    border: "border-[#D6BFE8]",
    badgeCls: "np-badge-neutral",
    rowBorder: "border-[#D6BFE8]",
  },
  {
    key: "despido",
    title: "Despido",
    subtitle: "Termina la relación laboral. El empleado pasa a estado Inactivo.",
    Icon: UserX,
    color: "text-error",
    bg: "bg-[#FDEDED]",
    border: "border-[#F0C6C6]",
    badgeCls: "np-badge-error",
    rowBorder: "border-[#B85252]",
  },
  {
    key: "permiso_sin_goce",
    title: "Permiso sin goce salarial",
    subtitle: "Autorización de ausencia por un período sin generar salario. El empleado se mantiene activo.",
    Icon: Coffee,
    color: "text-[#8A6D18]",
    bg: "bg-[#FFF7E0]",
    border: "border-[#E5D091]",
    badgeCls: "np-badge-warning",
    rowBorder: "border-[#E5D091]",
  },
  {
    key: "dia_libre_sin_goce",
    title: "Día Libre Sin Goce Salarial",
    subtitle: "Un día específico sin goce salarial. Requiere adjuntar el comprobante de solicitud.",
    Icon: CalendarDays,
    color: "text-[#8A6D18]",
    bg: "bg-[#FFF7E0]",
    border: "border-[#E5D091]",
    badgeCls: "np-badge-warning",
    rowBorder: "border-[#E5D091]",
  },
  {
    key: "aumento_salario",
    title: "Aumento de Salario",
    subtitle: "Incremento salarial que aplica desde la fecha indicada. Las planillas anteriores no se ven afectadas.",
    Icon: TrendingUp,
    color: "text-primary-color",
    bg: "bg-[#E6EFEA]",
    border: "border-primary-c",
    badgeCls: "np-badge-info",
    rowBorder: "border-primary-c",
  },
];

const TIPO_MAP = Object.fromEntries(ACCION_TIPOS.map(t => [t.key, t]));

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function daysBetween(a, b) {
  const A = new Date(a + "T00:00:00");
  const B = new Date(b + "T00:00:00");
  return Math.ceil((A - B) / (1000 * 60 * 60 * 24));
}

export default function AccionesPersonal() {
  const [empleados, setEmpleados] = useState([]);
  const [acciones, setAcciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [filtro, setFiltro] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [emps, acc] = await Promise.all([
        api.get("/empleados"),
        api.get("/acciones-personal"),
      ]);
      setEmpleados(emps.data || []);
      setAcciones(acc.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const empMap = useMemo(() => new Map(empleados.map(e => [e.id, e])), [empleados]);

  const filtered = useMemo(() => {
    const q = filtro.trim().toLowerCase();
    return acciones.filter(a => {
      if (filtroTipo !== "all" && a.tipo !== filtroTipo) return false;
      if (!q) return true;
      const emp = empMap.get(a.empleado_id);
      const hay = `${emp?.nombre || ""} ${emp?.apellidos || ""} ${a.puesto_nuevo || ""} ${a.puesto_anterior || ""} ${a.motivo || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [acciones, filtro, filtroTipo, empMap]);

  const eliminar = async (a) => {
    if (!window.confirm("¿Eliminar este registro de acción de personal? (No revierte los cambios al empleado; solo borra el registro histórico.)")) return;
    try {
      await api.delete(`/acciones-personal/${a.id}`);
      toast.success("Registro eliminado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const confirmarAscenso = async (a) => {
    if (!window.confirm("¿Confirmar el ascenso? El empleado mantiene el nuevo puesto y salario de forma definitiva.")) return;
    try {
      await api.put(`/acciones-personal/${a.id}`, { estado: "confirmado" });
      toast.success("Ascenso confirmado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo confirmar");
    }
  };

  const revertirAscenso = async (a) => {
    const emp = empMap.get(a.empleado_id);
    if (!window.confirm(`¿Revertir el ascenso de ${emp?.nombre || "este empleado"}? Se restaurará el puesto "${a.puesto_anterior || "—"}" y el salario ₡${(a.salario_anterior || 0).toLocaleString("es-CR")}.`)) return;
    try {
      await api.put(`/acciones-personal/${a.id}`, { estado: "revertido" });
      toast.success("Ascenso revertido — puesto y salario restaurados");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo revertir");
    }
  };

  return (
    <Layout>
      <header className="mb-8 fade-in-up">
        <div className="text-xs uppercase tracking-widest text-secondary-c font-semibold mb-2">Acciones de Personal</div>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-4xl sm:text-5xl font-semibold leading-tight">Acciones de Personal</h1>
            <p className="text-sm text-secondary-c mt-2">Registrá y llevá el historial de ascensos, cambios de puesto, descensos, renuncias y despidos.</p>
          </div>
          <button
            onClick={() => setOpenForm(true)}
            className="np-btn np-btn-primary"
            data-testid="accion-hr-new-btn"
          >
            <Plus size={16} /> Nueva acción
          </button>
        </div>
      </header>

      <div className="np-card mb-5 flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px] relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-c" />
          <input
            className="np-input pl-9"
            placeholder="Buscar por trabajador, puesto o motivo…"
            value={filtro}
            onChange={e => setFiltro(e.target.value)}
            data-testid="accion-hr-search"
          />
        </div>
        <select
          className="np-input max-w-[180px]"
          value={filtroTipo}
          onChange={e => setFiltroTipo(e.target.value)}
          data-testid="accion-hr-filter-tipo"
        >
          <option value="all">Todos</option>
          {ACCION_TIPOS.map(t => (
            <option key={t.key} value={t.key}>{t.title}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="np-card text-center text-sm text-secondary-c py-10">Cargando…</div>
      ) : filtered.length === 0 ? (
        <div className="np-card text-center py-16" data-testid="accion-hr-empty">
          <UserCog size={40} className="mx-auto text-secondary-c mb-3 opacity-60" />
          <div className="font-medium text-primary-c">Sin acciones registradas</div>
          <div className="text-xs text-secondary-c mt-1">Cuando registrés una acción de personal aparecerá acá.</div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="accion-hr-list">
          {filtered.map(a => (
            <AccionRow
              key={a.id}
              accion={a}
              empleado={empMap.get(a.empleado_id)}
              onConfirmar={confirmarAscenso}
              onRevertir={revertirAscenso}
              onEliminar={eliminar}
            />
          ))}
        </div>
      )}

      {openForm && (
        <AccionModal
          empleados={empleados}
          onClose={() => setOpenForm(false)}
          onSaved={() => { setOpenForm(false); load(); }}
        />
      )}
    </Layout>
  );
}

function AccionRow({ accion: a, empleado, onConfirmar, onRevertir, onEliminar }) {
  const displayName = empleado ? `${empleado.nombre} ${empleado.apellidos || ""}`.trim() : (a.empleado_id || "—");
  const isAscenso = a.tipo === "ascenso";
  const meta = TIPO_MAP[a.tipo] || TIPO_MAP.despido;
  const MetaIcon = meta.Icon;
  const showsPuesto = a.tipo === "ascenso" || a.tipo === "cambio_puesto" || a.tipo === "descenso";
  const estadoBadge = {
    activo: { label: "En prueba", cls: "np-badge-warning" },
    confirmado: { label: "Confirmado", cls: "np-badge-success" },
    revertido: { label: "Revertido", cls: "np-badge-neutral" },
    aplicado: { label: "Aplicado", cls: "np-badge-neutral" },
  }[a.estado] || { label: a.estado, cls: "np-badge-neutral" };

  // Info de período de prueba (solo ascenso)
  let periodoInfo = null;
  if (isAscenso && a.fin_periodo_prueba) {
    const dias = daysBetween(a.fin_periodo_prueba, todayISO());
    if (a.estado === "activo") {
      if (dias < 0) {
        periodoInfo = <span className="text-error text-xs font-medium">Período vencido hace {-dias} d</span>;
      } else if (dias <= 15) {
        periodoInfo = <span className="text-warning text-xs font-medium">Vence en {dias} d — Requiere revisión</span>;
      } else {
        periodoInfo = <span className="text-secondary-c text-xs">Fin de prueba: {fmtFecha(a.fin_periodo_prueba)} ({dias} d)</span>;
      }
    } else {
      periodoInfo = <span className="text-secondary-c text-xs">Fin de prueba: {fmtFecha(a.fin_periodo_prueba)}</span>;
    }
  }

  return (
    <div className={`np-card border-l-4 ${meta.rowBorder}`} data-testid={`accion-hr-row-${a.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-full bg-[#F0F0EA] text-primary-c flex items-center justify-center text-xs font-semibold flex-shrink-0">
          {initials(displayName)}
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-medium">{displayName}</div>
            <span className={`np-badge text-[10px] ${meta.badgeCls}`}>
              <MetaIcon size={10} className="inline mr-1" /> {meta.title}
            </span>
            <span className={`np-badge text-[10px] ${estadoBadge.cls}`}>{estadoBadge.label}</span>
          </div>
          <div className="text-xs text-secondary-c mt-1 flex items-center gap-1 flex-wrap">
            <Calendar size={11} /> {fmtFecha(a.fecha)}
            {periodoInfo && <span className="mx-1 opacity-40">•</span>}
            {periodoInfo}
          </div>
          {showsPuesto ? (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
              <div className="p-2 rounded-lg bg-[#FAFAF7] border border-c">
                <div className="text-[10px] uppercase tracking-wider text-secondary-c">Puesto</div>
                <div className="font-medium">{a.puesto_anterior || "—"} → <span className="text-primary-c">{a.puesto_nuevo || "—"}</span></div>
              </div>
              <div className="p-2 rounded-lg bg-[#FAFAF7] border border-c">
                <div className="text-[10px] uppercase tracking-wider text-secondary-c">Salario</div>
                <div className="font-medium">{fmtCurrency(a.salario_anterior || 0)} → <span className="text-primary-c">{fmtCurrency(a.salario_nuevo || 0)}</span></div>
              </div>
            </div>
          ) : null}
          {a.tipo === "aumento_salario" && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
              <div className="p-2 rounded-lg bg-[#FAFAF7] border border-c">
                <div className="text-[10px] uppercase tracking-wider text-secondary-c">Salario</div>
                <div className="font-medium">{fmtCurrency(a.salario_anterior || 0)} → <span className="text-primary-color font-semibold">{fmtCurrency(a.salario_nuevo || 0)}</span></div>
              </div>
              {a.porcentaje != null && (
                <div className="p-2 rounded-lg bg-[#FAFAF7] border border-c">
                  <div className="text-[10px] uppercase tracking-wider text-secondary-c">Incremento</div>
                  <div className="font-medium text-primary-color">+{a.porcentaje}% ({fmtCurrency((a.salario_nuevo || 0) - (a.salario_anterior || 0))})</div>
                </div>
              )}
            </div>
          )}
          {(a.tipo === "permiso_sin_goce" || a.tipo === "dia_libre_sin_goce") && a.fecha_desde && (
            <div className="mt-2 text-xs text-secondary-c">
              {a.tipo === "dia_libre_sin_goce" ? (
                <span>Día: <strong className="text-primary-c">{fmtFecha(a.fecha_desde)}</strong></span>
              ) : (
                <span>Desde <strong className="text-primary-c">{fmtFecha(a.fecha_desde)}</strong> hasta <strong className="text-primary-c">{fmtFecha(a.fecha_hasta)}</strong></span>
              )}
            </div>
          )}
          {(a.motivo || a.notas) && (
            <div className="mt-2 text-xs text-primary-c">
              {a.motivo && <div><strong>Motivo:</strong> {a.motivo}</div>}
              {a.notas && <div className="text-secondary-c mt-0.5">{a.notas}</div>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {isAscenso && a.estado === "activo" && (
            <>
              <button
                onClick={() => onConfirmar(a)}
                className="np-btn np-btn-outline text-xs py-1.5 px-2 border-primary-c text-primary-c hover:bg-[#E6EFEA]"
                title="Confirmar ascenso"
                data-testid={`accion-hr-confirm-${a.id}`}
              >
                <CheckCircle2 size={13} /> Confirmar
              </button>
              <button
                onClick={() => onRevertir(a)}
                className="np-btn np-btn-outline text-xs py-1.5 px-2 border-[#F0C6C6] text-error hover:bg-[#FDEDED]"
                title="Revertir ascenso"
                data-testid={`accion-hr-revert-${a.id}`}
              >
                <RotateCcw size={13} /> Revertir
              </button>
            </>
          )}
          <button
            onClick={() => onEliminar(a)}
            className="np-btn np-btn-ghost p-2 text-error hover:bg-[#FDEDED]"
            title="Eliminar registro"
            data-testid={`accion-hr-delete-${a.id}`}
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}

function AccionModal({ empleados, onClose, onSaved }) {
  const [tipo, setTipo] = useState("ascenso");
  const [empleadoId, setEmpleadoId] = useState("");
  const [fecha, setFecha] = useState(todayISO());
  const [puestoNuevo, setPuestoNuevo] = useState("");
  const [salarioNuevo, setSalarioNuevo] = useState("");
  const [motivo, setMotivo] = useState("");
  const [notas, setNotas] = useState("");
  const [fechaDesde, setFechaDesde] = useState(todayISO());
  const [fechaHasta, setFechaHasta] = useState(todayISO());
  const [adjunto, setAdjunto] = useState(null); // { fileName, base64 }
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const emp = empleados.find(e => e.id === empleadoId);
  const activos = empleados.filter(e => e.estado !== "Inactivo");

  const onPickAdjunto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.includes("pdf") && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Solo se admiten archivos PDF"); e.target.value = ""; return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("El archivo no debe superar los 10 MB"); e.target.value = ""; return;
    }
    const buf = await file.arrayBuffer();
    const b64 = btoa(new Uint8Array(buf).reduce((s, b) => s + String.fromCharCode(b), ""));
    setAdjunto({ fileName: file.name, base64: b64 });
    toast.success(`Documento adjuntado: ${file.name}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      const payload = {
        tipo, empleado_id: empleadoId, fecha, motivo, notas,
      };
      if (tipo === "ascenso" || tipo === "cambio_puesto" || tipo === "descenso") {
        payload.puesto_nuevo = puestoNuevo;
        if (salarioNuevo !== "" && salarioNuevo != null) {
          payload.salario_nuevo = Number(salarioNuevo);
        }
      }
      if (tipo === "permiso_sin_goce") {
        payload.fecha_desde = fechaDesde;
        payload.fecha_hasta = fechaHasta;
      }
      if (tipo === "aumento_salario") {
        if (!salarioNuevo || Number(salarioNuevo) <= 0) {
          setSaving(false);
          setError("El nuevo salario es requerido y debe ser mayor a 0.");
          return;
        }
        const salActual = Number(emp?.salario_base || 0);
        if (Number(salarioNuevo) <= salActual) {
          setSaving(false);
          setError(`El nuevo salario debe ser mayor al actual (₡${salActual.toLocaleString("es-CR")}). Si es una reducción, registrá un descenso de puesto.`);
          return;
        }
        payload.salario_nuevo = Number(salarioNuevo);
      }
      if (tipo === "dia_libre_sin_goce" && !adjunto) {
        setSaving(false);
        setError("El comprobante de solicitud (PDF) es obligatorio para un día libre sin goce salarial.");
        return;
      }
      if (adjunto) {
        payload.adjunto_pdf_base64 = adjunto.base64;
        payload.adjunto_file_name = adjunto.fileName;
      }
      await api.post("/acciones-personal", payload);
      const successMsgs = {
        ascenso: "Ascenso registrado — período de prueba de 3 meses iniciado",
        cambio_puesto: "Cambio de puesto registrado",
        descenso: "Descenso registrado — puesto y salario actualizados",
        renuncia: "Renuncia registrada — empleado marcado como Inactivo",
        despido: "Despido registrado — empleado marcado como Inactivo",
        permiso_sin_goce: "Permiso sin goce salarial registrado",
        dia_libre_sin_goce: "Día libre sin goce salarial registrado",
        aumento_salario: "Aumento de salario registrado — aplica desde la fecha indicada",
      };
      toast.success(successMsgs[tipo] || "Acción registrada");
      onSaved();
    } catch (err) {
      const msg = err?.response?.data?.detail || "No se pudo guardar la acción";
      setError(typeof msg === "string" ? msg : "Error de validación");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-xl shadow-2xl max-h-[92vh] overflow-hidden flex flex-col fade-in-up" onClick={(e) => e.stopPropagation()} data-testid="accion-hr-modal">
        <div className="p-5 border-b border-c flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserCog size={18} className="text-primary-color" />
            <h3 className="font-display text-xl font-semibold">Nueva acción de personal</h3>
          </div>
          <button onClick={onClose} className="np-btn np-btn-ghost p-2" data-testid="accion-hr-modal-close">✕</button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4 overflow-y-auto flex-1">
          {/* Tipo */}
          <div>
            <label className="np-label">Tipo de acción *</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {ACCION_TIPOS.map(opt => {
                const Icon = opt.Icon;
                const checked = tipo === opt.key;
                return (
                  <label
                    key={opt.key}
                    className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${checked ? `${opt.border} ${opt.bg}` : "border-c hover:bg-[#FAFAF7]"}`}
                    data-testid={`accion-hr-tipo-${opt.key}`}
                  >
                    <input type="radio" name="tipo" checked={checked} onChange={() => setTipo(opt.key)} className="mt-0.5" />
                    <div className="flex-1">
                      <div className={`font-medium text-sm flex items-center gap-2 ${opt.color}`}>
                        <Icon size={14} /> {opt.title}
                      </div>
                      <div className="text-xs text-secondary-c mt-0.5">{opt.subtitle}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Empleado */}
          <div>
            <label className="np-label">Trabajador *</label>
            <select
              className="np-input"
              required
              value={empleadoId}
              onChange={e => {
                const v = e.target.value;
                setEmpleadoId(v);
                const found = empleados.find(x => x.id === v);
                if (found && (tipo === "ascenso" || tipo === "cambio_puesto" || tipo === "descenso") && !salarioNuevo) {
                  setSalarioNuevo(String(found.salario_base || ""));
                }
              }}
              data-testid="accion-hr-empleado"
            >
              <option value="">Seleccionar trabajador…</option>
              {activos.map(e => (
                <option key={e.id} value={e.id}>{e.nombre} {e.apellidos || ""} · {e.puesto || "—"}</option>
              ))}
            </select>
            {emp && (
              <div className="text-xs text-secondary-c mt-1">
                Puesto actual: <strong className="text-primary-c">{emp.puesto || "—"}</strong> · Salario actual: <strong className="text-primary-c">{fmtCurrency(emp.salario_base || 0)}</strong>
              </div>
            )}
          </div>

          {/* Fecha */}
          <div>
            <label className="np-label">Fecha *</label>
            <input type="date" className="np-input" required value={fecha} onChange={e => setFecha(e.target.value)} data-testid="accion-hr-fecha" />
          </div>

          {(tipo === "ascenso" || tipo === "cambio_puesto" || tipo === "descenso") && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="np-label">Nuevo puesto *</label>
                  <input className="np-input" required value={puestoNuevo} onChange={e => setPuestoNuevo(e.target.value)} placeholder="Ej: Supervisor" data-testid="accion-hr-puesto-nuevo" />
                </div>
                <div>
                  <label className="np-label">
                    Nuevo salario base {tipo === "cambio_puesto" ? <span className="text-xs text-secondary-c font-normal">(opcional)</span> : "*"}
                  </label>
                  <input
                    type="number"
                    step="1000"
                    min="0"
                    className="np-input"
                    required={tipo !== "cambio_puesto"}
                    value={salarioNuevo}
                    onChange={e => setSalarioNuevo(e.target.value)}
                    placeholder={tipo === "cambio_puesto" ? "Dejar vacío para mantener el actual" : "Ej: 750000"}
                    data-testid="accion-hr-salario-nuevo"
                  />
                </div>
              </div>
              {tipo === "ascenso" && (
                <div className="p-3 rounded-xl border border-[#F0DDA0] bg-[#FFF8E1] text-xs text-primary-c flex items-start gap-2">
                  <ShieldCheck size={14} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Período de prueba (3 meses):</strong> a partir de la fecha, el sistema activará una alerta 15 días antes del cumplimiento para revisar si el ascenso se mantiene o se revierte.
                  </div>
                </div>
              )}
              {tipo === "cambio_puesto" && (
                <div className="p-3 rounded-xl border border-[#B7D4EA] bg-[#E7F1F9] text-xs text-primary-c flex items-start gap-2">
                  <ArrowLeftRight size={14} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Cambio lateral definitivo:</strong> el puesto se actualiza de forma inmediata. Sin período de prueba. Si dejás el salario vacío, se mantiene el actual.
                  </div>
                </div>
              )}
              {tipo === "descenso" && (
                <div className="p-3 rounded-xl border border-[#F0DDA0] bg-[#FFF3E0] text-xs text-primary-c flex items-start gap-2">
                  <ArrowDownCircle size={14} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Descenso definitivo:</strong> el puesto y salario se actualizan de forma inmediata. Verificá que el nuevo salario no sea menor al mínimo legal aplicable.
                  </div>
                </div>
              )}
            </>
          )}

          {tipo === "despido" && (
            <div className="p-3 rounded-xl border border-[#F0C6C6] bg-[#FDEDED] text-xs text-error flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
              <div>
                Esta acción marca al empleado como <strong>Inactivo</strong> y guarda la fecha de salida. Podés generar la Liquidación desde el módulo correspondiente.
              </div>
            </div>
          )}

          {tipo === "renuncia" && (
            <div className="p-3 rounded-xl border border-[#D6BFE8] bg-[#F1E8F7] text-xs text-primary-c flex items-start gap-2">
              <UserMinus size={14} className="mt-0.5 flex-shrink-0" />
              <div>
                La renuncia es una decisión del trabajador. El empleado quedará <strong>Inactivo</strong> a partir de la fecha indicada y podés generar la Liquidación correspondiente (sin preaviso ni cesantía, según Art. 28 CT).
              </div>
            </div>
          )}

          {tipo === "permiso_sin_goce" && (
            <>
              <div className="p-3 rounded-xl bg-[#FFF7E0] border border-[#E5D091] text-sm text-[#7A5C10] flex items-start gap-2">
                <Coffee size={14} className="mt-0.5 flex-shrink-0" />
                <div>
                  El empleado se mantiene <strong>activo</strong> pero no genera salario durante el rango indicado. Se guarda en su expediente con el respaldo adjunto.
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="np-label">Desde *</label>
                  <input type="date" className="np-input" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} required data-testid="accion-hr-fecha-desde" />
                </div>
                <div>
                  <label className="np-label">Hasta *</label>
                  <input type="date" className="np-input" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} min={fechaDesde} required data-testid="accion-hr-fecha-hasta" />
                </div>
              </div>
            </>
          )}

          {tipo === "dia_libre_sin_goce" && (
            <div className="p-3 rounded-xl bg-[#FFF7E0] border border-[#E5D091] text-sm text-[#7A5C10] flex items-start gap-2">
              <CalendarDays size={14} className="mt-0.5 flex-shrink-0" />
              <div>
                Registra <strong>un día específico</strong> sin goce salarial. El empleado sigue activo. <strong>Es obligatorio adjuntar el comprobante de solicitud</strong> (PDF).
              </div>
            </div>
          )}

          {tipo === "aumento_salario" && (
            <>
              <div className="p-3 rounded-xl bg-[#E6EFEA] border border-primary-c text-sm text-primary-c flex items-start gap-2">
                <TrendingUp size={14} className="mt-0.5 flex-shrink-0" />
                <div>
                  El aumento aplica <strong>a partir de la fecha indicada</strong>. Las planillas, aguinaldos y liquidaciones de <strong>períodos anteriores no se ven afectados</strong> — mantienen el salario histórico. Queda registro permanente en el expediente del trabajador.
                </div>
              </div>
              <div>
                <label className="np-label">Nuevo salario base *</label>
                <input
                  type="number"
                  step="1000"
                  min="0"
                  className="np-input"
                  required
                  value={salarioNuevo}
                  onChange={e => setSalarioNuevo(e.target.value)}
                  placeholder={emp ? `Actual: ₡${Number(emp.salario_base || 0).toLocaleString("es-CR")}` : "Seleccioná primero al trabajador"}
                  data-testid="accion-hr-aumento-salario"
                />
                {emp && salarioNuevo && Number(salarioNuevo) > Number(emp.salario_base || 0) && (
                  <div className="text-xs text-primary-color mt-1">
                    Incremento: <strong>₡{(Number(salarioNuevo) - Number(emp.salario_base || 0)).toLocaleString("es-CR")}</strong>
                    {" "}
                    ({(((Number(salarioNuevo) - Number(emp.salario_base || 0)) / Number(emp.salario_base || 1)) * 100).toFixed(2)}%)
                  </div>
                )}
              </div>
            </>
          )}

          <div>
            <label className="np-label flex items-center gap-1.5">
              <Upload size={12} />
              {tipo === "dia_libre_sin_goce"
                ? "Comprobante de solicitud (PDF) *"
                : "Documento de respaldo (PDF, opcional)"}
            </label>
            <p className="text-[11px] text-secondary-c mb-2">
              {tipo === "dia_libre_sin_goce"
                ? "Solicitud firmada por el trabajador y/o autorización correspondiente. Máx. 10 MB."
                : "Autorización firmada, carta de renuncia, constancia, etc. Máx. 10 MB."}
            </p>
            {adjunto ? (
              <div className="flex items-center justify-between gap-3 p-3 bg-[#E6EFEA] rounded-xl">
                <div className="flex items-center gap-2 text-sm min-w-0">
                  <FileText size={16} className="text-primary-color flex-shrink-0" />
                  <span className="truncate" data-testid="accion-hr-adjunto-name">{adjunto.fileName}</span>
                </div>
                <button type="button" onClick={() => setAdjunto(null)} className="np-btn np-btn-ghost text-xs" data-testid="accion-hr-adjunto-remove">Quitar</button>
              </div>
            ) : (
              <label className="np-btn np-btn-outline w-full justify-center cursor-pointer" data-testid="accion-hr-adjunto-label">
                <Upload size={14} className="mr-1.5" /> Elegir PDF
                <input type="file" accept="application/pdf,.pdf" onChange={onPickAdjunto} className="hidden" data-testid="accion-hr-adjunto-input" />
              </label>
            )}
          </div>

          <div>
            <label className="np-label">Motivo</label>
            <input className="np-input" value={motivo} onChange={e => setMotivo(e.target.value)} placeholder={
              tipo === "ascenso" ? "Ej: Buen desempeño" :
              tipo === "cambio_puesto" ? "Ej: Reorganización interna" :
              tipo === "descenso" ? "Ej: Reasignación por rendimiento" :
              tipo === "renuncia" ? "Ej: Oportunidad laboral externa" :
              tipo === "permiso_sin_goce" ? "Ej: Asuntos personales, viaje familiar" :
              "Ej: Bajo desempeño reiterado"
            } data-testid="accion-hr-motivo" />
          </div>

          <div>
            <label className="np-label">Notas</label>
            <textarea className="np-input min-h-[70px]" value={notas} onChange={e => setNotas(e.target.value)} placeholder="Detalles adicionales opcionales…" data-testid="accion-hr-notas" />
          </div>

          {error && (
            <div className="p-3 rounded-xl bg-[#FDEDED] border border-[#F0C6C6] text-sm text-error" data-testid="accion-hr-modal-error">{error}</div>
          )}
        </form>

        <div className="p-4 border-t border-c flex justify-end gap-2 bg-white">
          <button type="button" onClick={onClose} className="np-btn np-btn-ghost" data-testid="accion-hr-modal-cancel">Cancelar</button>
          <button type="button" onClick={submit} disabled={saving} className="np-btn np-btn-primary" data-testid="accion-hr-modal-save">
            {saving ? "Guardando…" : "Registrar acción"}
          </button>
        </div>
      </div>
    </div>
  );
}
